/**
 * Enhanced Display Name Generation for Indicators
 *
 * Generates display names that include source indicator information
 * for series-as-source dependencies.
 *
 * This is the SINGLE SOURCE OF TRUTH for indicator display names.
 * All other components should use these utilities instead of creating their own.
 *
 * @module indicatorDisplayName
 */

import { extractSourceIndicatorId } from "./indicatorDependencyValidator.js";
import { isSeriesSource } from "./seriesSource.js";

/**
 * Get the base display name for an indicator from its config
 *
 * This function generates compact display names based on indicator parameters.
 * It does NOT include source context - use generateEnhancedDisplayName() for that.
 *
 * @param {Object} indicator - Indicator configuration
 * @returns {string} Base display name (e.g., "EMA(9)", "MACD(12,26,9)")
 *
 * @example
 * getBaseDisplayName({ key: 'ema', displayName: 'EMA', config: { windowSize: 9 } })
 * // Returns: "EMA(9)"
 *
 * @example
 * getBaseDisplayName({ key: 'macd', config: { fast: 12, slow: 26, signal: 9 } })
 * // Returns: "MACD(12,26,9)"
 */
export function getBaseDisplayName(indicator) {
  const key = indicator.key || "unknown";
  const config = indicator.config || {};
  const keyUpper = key.toUpperCase();

  // Volume indicator with volumePath parameter
  if (key === "volume") {
    if (config.volumePath) {
      const volumeTypeMap = {
        volume: "Qtd",
        financialVolume: "Fin",
        tickCount: "Neg",
      };
      const volumeType = volumeTypeMap[config.volumePath] || "Qtd";
      return `Volume (${volumeType})`;
    }
    // Fallback for old Volume indicators without volumePath
    return "Volume (Qtd)";
  }

  // SAR with acceleration parameters
  if (
    key === "sar" &&
    config.accelerationFactor !== undefined &&
    config.maxAccelerationFactor !== undefined
  ) {
    return `${keyUpper}(${config.accelerationFactor},${config.maxAccelerationFactor})`;
  }

  // Indicators that don't show parameters (complex multi-param indicators)
  const noParamIndicators = [
    "vwap",
    "ichimoku",
    "alligator",
    "supertrend",
    "atrStop",
  ];
  if (noParamIndicators.includes(key)) {
    return keyUpper;
  }

  // MACD-style (fast, slow, signal)
  if (config.fast && config.slow && config.signal) {
    return `${keyUpper}(${config.fast},${config.slow},${config.signal})`;
  }

  // Bollinger Bands and similar (windowSize, multiplier)
  if (config.windowSize && config.multiplier && !config.slow && !config.fast) {
    return `${keyUpper}(${config.windowSize},${config.multiplier})`;
  }

  // Simple indicators with just windowSize (EMA, SMA, RSI, etc.)
  if (config.windowSize && !config.slow && !config.fast && !config.multiplier) {
    return `${keyUpper}(${config.windowSize})`;
  }

  // Period-based indicators (some use 'period' instead of 'windowSize')
  if (
    config.period &&
    !config.slow &&
    !config.fast &&
    !config.multiplier &&
    !config.windowSize
  ) {
    return `${keyUpper}(${config.period})`;
  }

  // Fallback to uppercase key (avoid using displayName as it might be too verbose)
  return keyUpper;
}

/**
 * Find source indicator display name from chart indicators
 * @param {string} sourcePath - Source path (e.g., "volume_123.volume")
 * @param {Array} chartIndicators - All indicators on chart
 * @returns {string|null} Source indicator display name or null
 *
 * @example
 * findSourceDisplayName("volume_123.volume", indicators)
 * // Returns: "VOLUME"
 *
 * @example
 * findSourceDisplayName("ema(10).ema", indicators)
 * // Returns: "EMA(10,VOLUME)" if EMA has displayName set with source context
 */
function findSourceDisplayName(sourcePath, chartIndicators) {
  if (!sourcePath || !chartIndicators) return null;

  const sourceIndicatorId = extractSourceIndicatorId(sourcePath);
  if (!sourceIndicatorId) return null;

  const sourceIndicator = chartIndicators.find(
    (ind) =>
      ind.renderKey === sourceIndicatorId || ind.id === sourceIndicatorId,
  );

  if (!sourceIndicator) return null;

  // Use source indicator's displayName if available (includes chain context)
  // Otherwise fall back to base display name
  return sourceIndicator.displayName || getBaseDisplayName(sourceIndicator);
}

/**
 * Generate enhanced display name for an indicator
 *
 * Rules:
 * - No series source: Use base name (e.g., "EMA(9)")
 * - OHLC source: Use base name (e.g., "EMA(9,CLOSE)")
 * - Series source: Include source name (e.g., "EMA(9,VOLUME)")
 *
 * @param {Object} indicator - Indicator configuration
 * @param {Array} chartIndicators - All indicators on chart
 * @returns {string} Enhanced display name
 *
 * @example
 * // No series source
 * generateEnhancedDisplayName(
 *   { key: 'ema', config: { windowSize: 9, sourcePath: 'close' } },
 *   []
 * )
 * // Returns: "EMA(9)"
 *
 * @example
 * // Series source
 * generateEnhancedDisplayName(
 *   { key: 'ema', config: { windowSize: 9, sourcePath: 'volume_123.volume' } },
 *   [{ renderKey: 'volume_123', key: 'volume', displayName: 'VOLUME' }]
 * )
 * // Returns: "EMA(9,VOLUME)"
 */
export function generateEnhancedDisplayName(indicator, chartIndicators = []) {
  const baseName = getBaseDisplayName(indicator);
  const sourcePath = indicator.config?.sourcePath;

  // No source path: use base name
  if (!sourcePath) {
    return baseName;
  }

  // Check if it's a series source (not OHLC)
  if (!isSeriesSource(sourcePath)) {
    // OHLC source - use base name (might already include source in params)
    return baseName;
  }

  // Find source indicator
  const sourceDisplayName = findSourceDisplayName(sourcePath, chartIndicators);

  if (!sourceDisplayName) {
    // Source not found - fallback to base name
    return baseName;
  }

  // Extract indicator params from base name to rebuild with source
  // Pattern: "INDICATOR(params)" → extract "INDICATOR" and "params"
  const match = baseName.match(/^([A-Z]+)\(([^)]+)\)$/);

  if (match) {
    const [, indicatorName, params] = match;
    // Rebuild as "INDICATOR(params,SOURCE)"
    return `${indicatorName}(${params},${sourceDisplayName})`;
  }

  // No params pattern found - just append source
  return `${baseName}(${sourceDisplayName})`;
}

/**
 * Update indicator displayName based on series source
 *
 * Mutates the indicator object to set enhanced displayName.
 * Safe to call multiple times (idempotent).
 *
 * @param {Object} indicator - Indicator to update (mutated)
 * @param {Array} chartIndicators - All indicators on chart
 * @returns {Object} The same indicator (mutated)
 *
 * @example
 * const indicator = {
 *   key: 'ema',
 *   displayName: 'EMA',
 *   config: { windowSize: 9, sourcePath: 'volume_123.volume' }
 * };
 * updateIndicatorDisplayName(indicator, chartIndicators);
 * // indicator.displayName is now "EMA(9,VOLUME)"
 */
export function updateIndicatorDisplayName(indicator, chartIndicators = []) {
  if (!indicator) return indicator;

  // Generate enhanced display name
  const enhancedName = generateEnhancedDisplayName(indicator, chartIndicators);

  // Update indicator
  indicator.displayName = enhancedName;

  return indicator;
}
